import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  computed,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';

import { ConfigService } from '../config/config.service';

const THEME_VERSION = 'v1.0.1';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly configService = inject(ConfigService);

  private readonly activeTheme = signal<string | null>(null);

  // Subject to emit when theme CSS is loaded
  private readonly themeLoadedSubject = new Subject<boolean>();
  readonly themeLoaded$ = this.themeLoadedSubject.asObservable();

  readonly availableThemes = signal<string[]>([]);
  readonly sortedThemes = computed(() => [...this.availableThemes()].sort());
  readonly currentTheme = computed(() => this.activeTheme());

  // Computed to get the CSS class of the theme from config
  readonly themeClass = computed(() => {
    const theme = this.configService.activeClientId();
    return theme ? `${theme}-theme` : '';
  });

  constructor() {
    // If we're on the server, we might already have the link injected by loadThemeCSS
    // so we can initialize the subject as true if we find any theme link.
    if (!isPlatformBrowser(this.platformId)) {
      const existingTheme = this.document.querySelector('link[id^="theme-"]');
      if (existingTheme) {
        this.themeLoadedSubject.next(true);
      }
    }
  }

  /**
   * Applies the theme from the current configuration
   */
  applyConfiguredTheme(): Observable<boolean> {
    const theme = this.configService.activeClientId();
    if (!theme) {
      this.themeLoadedSubject.next(true);
      return of(true);
    }

    this.removeAllThemeClasses();
    this.loadThemeCSS(theme);
    this.activeTheme.set(theme);
    this.document.body.classList.add(`${theme}-theme`);

    if (!isPlatformBrowser(this.platformId)) {
      this.themeLoadedSubject.next(true);
      return of(true);
    }

    return this.themeLoaded$.pipe(
      take(1),
      timeout(5000),
      catchError(() => {
        console.error(`Theme CSS loading timeout: ${theme}/theme.css`);
        return of(false);
      }),
    );
  }

  private removeAllThemeClasses(): void {
    const bodyElement = this.document.body;
    const currentTheme = this.activeTheme();

    if (currentTheme) {
      bodyElement.classList.remove(`${currentTheme}-theme`);
      this.unloadThemeCSS(currentTheme);
    }
  }

  private loadThemeCSS(theme: string): void {
    const linkId = `theme-${theme}`;
    const existingLink = this.document.getElementById(
      linkId,
    ) as HTMLLinkElement;

    if (existingLink) {
      if (isPlatformBrowser(this.platformId)) {
        // If it exists but we need to verify it's loaded
        setTimeout(() => {
          const hasStyles = this.verifyThemeLoaded(theme);
          this.themeLoadedSubject.next(hasStyles);
        }, 100);
      } else {
        this.themeLoadedSubject.next(true);
      }
      return;
    }

    if (isPlatformBrowser(this.platformId)) {
      this.unloadAllThemeCSS();
    }

    const link = this.document.createElement('link');
    link.rel = 'stylesheet';
    const version = isPlatformBrowser(this.platformId)
      ? `?v=${THEME_VERSION}`
      : '';
    link.href = `/assets/clients/${theme}/theme.css${version}`;
    link.id = linkId;

    if (isPlatformBrowser(this.platformId)) {
      link.onload = () => {
        setTimeout(() => {
          const hasStyles = this.verifyThemeLoaded(theme);
          this.themeLoadedSubject.next(hasStyles);
        }, 100);
      };

      link.onerror = () => {
        console.error(`Failed to load theme CSS: ${theme}/theme.css`);
        this.themeLoadedSubject.next(false);
      };
    } else {
      this.themeLoadedSubject.next(true);
    }

    this.document.head.appendChild(link);
  }

  private unloadThemeCSS(theme: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.document.getElementById(`theme-${theme}`)?.remove();
  }

  private unloadAllThemeCSS(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    // Find any theme links and remove them
    const themeLinks = this.document.querySelectorAll('link[id^="theme-"]');
    themeLinks.forEach((link) => link.remove());
  }

  private verifyThemeLoaded(theme: string): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    const themeLink = this.document.getElementById(
      `theme-${theme}`,
    ) as HTMLLinkElement;
    if (!themeLink?.sheet) {
      return false;
    }

    try {
      const rules = themeLink.sheet.cssRules || themeLink.sheet.rules;
      return rules && rules.length > 0;
    } catch {
      return false;
    }
  }

  setThemeForDevelopment(theme: string): void {
    this.configService.loadConfig(theme).subscribe(() => {
      this.applyConfiguredTheme().subscribe();
    });
  }

  loadAvailableThemes(): void {
    const isLocal =
      !isPlatformBrowser(this.platformId) ||
      this.document.location.hostname === 'localhost' ||
      this.document.location.hostname === '127.0.0.1';

    if (isPlatformBrowser(this.platformId) && isLocal) {
      this.configService.http
        .get<string[]>('/assets/clients/themes.json')
        .subscribe({
          next: (themes) => this.availableThemes.set(themes),
          error: (err) =>
            console.warn(
              'Could not load themes manifest (this is expected in production):',
              err,
            ),
        });
    }
  }

  clearTheme(): void {
    this.configService.loadConfig().subscribe(() => {
      this.applyConfiguredTheme().subscribe();
    });
  }
}
